import { directInvitationDetailsQuery, useAcceptDirectInvitationMutation } from "@/api/directInvitation";
import {
  invitationDetailsQuery,
  useAcceptInvitationMutation,
  useCheckAcceptRequirementsForEmailMutation,
  useCheckDirectAcceptRequirementsForEmailMutation,
} from "@/api/invitation";
import { countryCapabilitiesQuery } from "@/api/location";
import { api } from "@/api/utils";
import { LocationPickerModal } from "@/components/LocationPickerModal";
import { InvitationErrorPage } from "@/components/invitations/InvitationErrorPage";
import { Button } from "@/components/ui/Button";
import { IconButton } from "@/components/ui/IconButton";
import { getErrorMessage } from "@/utils/error";
import { ApiErrorResponse } from "@common/api/api-types";
import {
  Box,
  Center,
  ErrorMessage,
  Field,
  Flex,
  Form,
  Input,
  Label,
  QueryHandler,
  Span,
  Spinner,
  Text,
} from "@givenwell/components";
import {
  ChevronLeftIcon,
  InvisibleIcon,
  MagnifyingGlassIcon,
  PadlockIcon,
  VisibleIcon,
} from "@givenwell/core-solid-icons";
import { createGlobalState, useGlobalState } from "@givenwell/hooks";
import {
  AcceptInvitationBody,
  GetDirectInvitationDetailsResponse,
  GetInvitationDetailsResponse,
} from "@givenwell/marketplace-api";
import { colors, easing } from "@givenwell/theme";
import { useMutation, UseMutationResult, useQuery, UseQueryResult } from "@tanstack/react-query";
import { createFileRoute, getRouteApi, Link, linkOptions, redirect, useNavigate } from "@tanstack/react-router";
import { FunctionComponent, lazy, ReactNode, Suspense, useEffect, useId, useMemo, useState } from "react";
import { fallback, number, object, optional, picklist, string } from "valibot";
import { Description, Title } from "./components";

export const Route = createFileRoute("/new-account")({
  component: Page,
  validateSearch: object({
    token: fallback(optional(string(), ""), ""),
    kind: picklist(["supporter", "direct"]),
    stepIdx: fallback(optional(number(), 0), 0),
  }),
  beforeLoad: ({ search }) => {
    return {
      token: search.token,
    };
  },
  loader: ({ context }) => {
    if (!context.token) {
      throw redirect({
        to: "/",
      });
    }
  },
});

const PasswordStrengthBar = lazy(() =>
  import("react-password-strength-bar").then(mod => {
    // @ts-expect-error package is LYING to me about its exports
    const component = "default" in mod.default ? mod.default.default : mod.default;

    return { default: component };
  }),
);

const route = getRouteApi("/new-account");

type Account = AcceptInvitationBody & { isMergingWithMgmtAcct: boolean };

type StepProps = {
  account: Account;
  onBack: () => void;
  onContinue: (account: Account) => void;
  onCreate: (account: Account) => void;
  status: UseMutationResult["status"];
  countryCode: string;
  details: GetInvitationDetailsResponse | GetDirectInvitationDetailsResponse;
};

type AvailabilityFn = (account: Account) => boolean;

type Step = {
  available: AvailabilityFn;
  component: FunctionComponent<StepProps>;
};

const emailIsComplete: AvailabilityFn = account => !!account.email;
const nameIsComplete: AvailabilityFn = account =>
  emailIsComplete(account) && !!account.given_name && !!account.family_name;
const passwordIsComplete: AvailabilityFn = account => nameIsComplete(account) && !!account.password;

const initialAccount: Account = {
  email: "",
  given_name: "",
  family_name: "",
  password: "",
  isMergingWithMgmtAcct: false,
  approximate_location: {
    coords: {
      latitude: 0,
      longitude: 0,
    },
    place_name: "",
  },
};

const accountState = createGlobalState(initialAccount);

function useDetailsQuery(kind: "supporter" | "direct", token: string) {
  const direct = useQuery({ ...directInvitationDetailsQuery(token), enabled: kind === "direct" && !!token });
  const supporter = useQuery({ ...invitationDetailsQuery(token), enabled: kind === "supporter" && !!token });
  return (kind === "direct" ? direct : supporter) as UseQueryResult<
    GetDirectInvitationDetailsResponse | GetInvitationDetailsResponse,
    Error
  >;
}

/**
 * Returns the initial email value and whether the field should be readonly.
 *
 * Priority:
 * 1. Direct invite → use details.email (readonly)
 * 2. Supporter + require_work_email → use details.email (readonly)
 * 3. Supporter + !require_work_email + prefill_email → use prefill_email (editable)
 * 4. Supporter + !require_work_email + no prefill → empty (editable)
 */
function getInitialEmail(
  kind: "supporter" | "direct",
  details: GetInvitationDetailsResponse | GetDirectInvitationDetailsResponse,
): { email: string; readonly: boolean } {
  // Direct invites always use the email from details
  if (kind === "direct") {
    return { email: details.email, readonly: true };
  }

  const supporterDetails = details as GetInvitationDetailsResponse;

  // Supporter with required work email - use work email, readonly
  if (supporterDetails.require_work_email) {
    return { email: details.email, readonly: true };
  }

  // Supporter without required work email - use prefill if available, editable
  return {
    email: supporterDetails.prefill_email ?? "",
    readonly: false,
  };
}

function Page() {
  const { token, stepIdx: rawStepIdx, kind } = route.useSearch();
  const detailsQuery = useDetailsQuery(kind, token);
  const countryCapsQuery = useQuery(countryCapabilitiesQuery(detailsQuery.data?.country_code));
  const navigate = route.useNavigate();

  const [account, setAccount] = useGlobalState(accountState);

  // Set initial values from invitation details.
  useEffect(() => {
    if (!detailsQuery.data) return;
    if (account.given_name) return; // Already initialized

    const { given_name, family_name } = detailsQuery.data;
    const { email } = getInitialEmail(kind, detailsQuery.data);

    setAccount({ ...account, email, given_name, family_name });
  }, [account, detailsQuery.data, kind, setAccount]);

  const STEPS = useMemo(() => {
    const steps: Step[] = [
      {
        available: () => true,
        component: EmailStep,
      },
      {
        available: emailIsComplete,
        component: DetailsStep,
      },
    ];
    if (!account.isMergingWithMgmtAcct) {
      steps.push({
        available: nameIsComplete,
        component: PasswordStep,
      });
    }
    steps.push({
      available: passwordIsComplete,
      component: LocationStep,
    });
    return steps;
  }, [account.isMergingWithMgmtAcct]);

  // Don't let people use browser history to skip steps
  const lastAvailableStepIdx = STEPS.findLastIndex(step => step.available(account));
  const stepIdx = Math.min(rawStepIdx || 0, lastAvailableStepIdx);

  const step = STEPS[stepIdx]!;

  function setStep(stepIdx: number) {
    navigate({
      search: {
        token,
        kind,
        stepIdx: Math.min(stepIdx, STEPS.length - 1),
      },
      mask: {
        search: {
          token,
          kind,
        },
      },
    });
  }

  const acceptMut = useAcceptInvitationMutation(token);
  const acceptDirectMut = useAcceptDirectInvitationMutation(token);

  function handleCreateAccount(account: Account) {
    const options = linkOptions({
      to: "/",
      search: {
        "first-time": true,
      },
      mask: {
        to: "/",
        search: {},
      },
    });

    const mut = kind === "direct" ? acceptDirectMut : acceptMut;

    if (mut.isPending || mut.isSuccess) return;
    setAccount(account);
    mut.mutate(account, {
      onSuccess() {
        navigate(options);
      },
    });
  }

  function handleBack() {
    if (stepIdx === 0) {
      const to = kind === "direct" ? "/gift/activate" : "/invitation/activate";
      navigate({
        to,
        search: {
          token: token,
        },
      });
    } else {
      setStep(stepIdx - 1);
    }
  }

  // Once the invitation is accepted, the "/" route's beforeLoad clears the query cache, which
  // refetches the invitation details and gets "already accepted" back. Hold a spinner here
  // until the navigation away completes so that error never surfaces.
  if (acceptMut.isSuccess || acceptDirectMut.isSuccess) {
    return (
      <Center css={{ h: "100%" }}>
        <Spinner />
      </Center>
    );
  }

  return (
    <QueryHandler
      query={detailsQuery}
      error={({ error: response }) => {
        const apiError = response as unknown as ApiErrorResponse;
        const errorCode = apiError.body?.code?.toString() || apiError?.status?.toString();

        return <InvitationErrorPage code={errorCode} token={token} />;
      }}
      success={({ data: details }) => {
        return (
          <TopBarLayout
            progress={(stepIdx + 1) / (STEPS.length + 1)}
            onBack={() => {
              const to = kind === "direct" ? "/gift/activate" : "/invitation/activate";
              if (stepIdx === 0) {
                navigate({
                  to,
                  search: {
                    token: token,
                  },
                });
              } else {
                setStep(stepIdx - 1);
              }
            }}
          >
            <step.component
              account={account}
              details={details}
              onBack={handleBack}
              onContinue={account => {
                setAccount(account);
                setStep(stepIdx + 1);
              }}
              countryCode={details.country_code}
              status={acceptMut.status}
              onCreate={handleCreateAccount}
            />
          </TopBarLayout>
        );
      }}
    />
  );
}

function TopBarLayout({ children, progress, onBack }: { children?: ReactNode; progress: number; onBack?: () => void }) {
  return (
    <Flex css={{ flexDir: "column", height: "100%" }}>
      <Box
        css={{
          p: 16,
          pl: "max(var(--safe-area-inset-right), 16px)",
          pt: "max(var(--safe-area-inset-top), 12px)",
          pb: 12,
          position: "relative",
        }}
      >
        <IconButton aria-label="Back" color="neutral" variant="plain" onClick={onBack}>
          <ChevronLeftIcon />
        </IconButton>
        <Box
          css={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 4, backgroundColor: colors.warmGrey }}
        />
        <Box
          css={{
            position: "absolute",
            bottom: 0,
            left: 0,
            height: 4,
            roundedR: 9999,
            backgroundColor: colors.blue800,
            transition: `width 0.2s ${easing.standard}`,
          }}
          style={{
            width: `${progress * 100}%`,
          }}
        />
      </Box>
      {children}
    </Flex>
  );
}

function StepLayout({ children, footer }: { children?: ReactNode; footer?: ReactNode }) {
  return (
    <>
      <Flex
        css={{
          flexDirection: "column",
          flex: "1 0 0px",
          overflowY: "auto",
          pl: "max(var(--safe-area-inset-left), 16px)",
          pr: "max(var(--safe-area-inset-right), 16px)",
          py: 32,
          mx: "auto",
          width: "100%",
          maxW: 600,
        }}
      >
        {children}
      </Flex>
      <Flex css={{ justify: "center", pl: "var(--safe-area-inset-left)", pr: "var(--safe-area-inset-right)" }}>
        <Flex
          css={{
            p: 16,
            pb: "max(var(--safe-area-inset-bottom), 16px)",
            flexDir: "column",
            gap: 16,
            boxShadow: "0px 0px 4px 4px rgba(0, 0, 0, 0.04)",
            width: "100%",
            maxW: 612,
            "@sm": {
              roundedT: 16,
            },
          }}
        >
          {footer}
        </Flex>
      </Flex>
    </>
  );
}

function EmailStep({ onContinue, account, details }: StepProps) {
  const id = useId();
  const { token, kind } = route.useSearch();

  const initialEmail = getInitialEmail(kind, details);
  const [email, setEmail] = useState(initialEmail.email || account.email);
  const [isAttemptingMergeWithMgmt, setIsAttemptingMergeWithMgmt] = useState(account.isMergingWithMgmtAcct);
  const [password, setPassword] = useState(account.password);
  const navigate = useNavigate();

  const regularMut = useCheckAcceptRequirementsForEmailMutation(token);
  const directMut = useCheckDirectAcceptRequirementsForEmailMutation(token);

  const mgmtPasswordCheckMutation = useMutation({
    mutationFn: async () => {
      const result = await api.auth.jsonLogin({
        application: "management",
        requestBody: {
          email,
          password,
        },
      });
      if (!result.access_token) {
        throw new Error("Invalid credentials");
      }
    },
    onError() {},
  });

  const mut = kind === "direct" ? directMut : regularMut;

  return (
    <StepLayout
      footer={
        <>
          <Text css={{ fontScale: "sm", color: colors.text2 }}>
            By using Givenwell, you agree to our{" "}
            <Link to="/policies/terms">
              <Span css={{ weight: 500, color: colors.navy, textDecoration: "underline" }}>terms of use</Span>
            </Link>
            .
          </Text>
          <Button
            variant="filled"
            css={{ w: "100%" }}
            size="lg"
            color="primary"
            type="submit"
            form={id}
            loading={mut.isPending || mgmtPasswordCheckMutation.isPending}
          >
            Continue
          </Button>
        </>
      }
    >
      <Title css={{ pb: 16 }}>{isAttemptingMergeWithMgmt ? "Enter Your Password" : "Your Account"}</Title>
      {isAttemptingMergeWithMgmt && (
        <Text css={{ color: colors.text2, pb: 16 }}>You have a Givenwell Account associated with this email.</Text>
      )}
      <form
        id={id}
        onSubmit={e => {
          e.preventDefault();
          if (!email) return;

          if (isAttemptingMergeWithMgmt) {
            mgmtPasswordCheckMutation.mutate(undefined, {
              onSuccess() {
                onContinue({
                  ...account,
                  isMergingWithMgmtAcct: true,
                  email,
                  password,
                });
              },
            });
            return;
          }

          mut.mutate(
            { email },
            {
              onSuccess(data) {
                if (data.accept_requirement === "not_acceptable") {
                  // Member account already exists
                  navigate({
                    to: "/merge-account",
                    search: {
                      kind: kind,
                      token: token,
                    },
                  });
                } else if (data.accept_requirement === "authentication_required") {
                  setIsAttemptingMergeWithMgmt(true);
                } else {
                  onContinue({
                    ...account,
                    email,
                  });
                }
              },
            },
          );
        }}
      >
        <Box css={{ spaceY: 24 }}>
          {initialEmail.readonly ? (
            <Field>
              <Label>Work Email</Label>
              <Text css={{ color: colors.text2, mb: 8 }}>
                This is the email address you will use to sign into Givenwell.
              </Text>
              <Box
                css={{
                  px: 16,
                  py: 12,
                  rounded: 8,
                  backgroundColor: colors.warmGrey,
                  color: colors.text2,
                  fontScale: "lg",
                }}
              >
                {initialEmail.email}
              </Box>
              <Text css={{ color: colors.text2, mt: 12, fontScale: "sm" }}>
                Not your email?{" "}
                <a href="mailto:support@givenwell.com" style={{ color: colors.navy, textDecoration: "underline" }}>
                  Contact support
                </a>
              </Text>
            </Field>
          ) : (
            <Field>
              <Label>Personal Email</Label>
              <Input
                required
                size="lg"
                type="email"
                autoComplete="email"
                placeholder="name@email.com"
                value={email}
                onValueChange={value => {
                  setEmail(value);
                  setIsAttemptingMergeWithMgmt(false);
                }}
              />
            </Field>
          )}
          {isAttemptingMergeWithMgmt && (
            <Field>
              <Label>Password</Label>
              <Input
                autoFocus
                required
                size="lg"
                type="password"
                autoComplete="current-password"
                placeholder="Enter your password"
                value={password}
                onValueChange={setPassword}
              />
              {mgmtPasswordCheckMutation.error && (
                <ErrorMessage>{getErrorMessage(mgmtPasswordCheckMutation.error)}</ErrorMessage>
              )}
            </Field>
          )}
          {"supporter_display_name" in details && !isAttemptingMergeWithMgmt && !initialEmail.readonly ? (
            <Text css={{ color: colors.text2 }}>
              We recommend using a personal email address for your login to keep your emails with Givenwell private from
              your employer. Your account stays with you if you change jobs, and your email address won't be shared with{" "}
              {details.supporter_display_name}.
            </Text>
          ) : null}
        </Box>
      </form>
    </StepLayout>
  );
}

function DetailsStep({ onContinue, account }: StepProps) {
  const id = useId();
  const [givenName, setGivenName] = useState(account.given_name);
  const [familyName, setFamilyName] = useState(account.family_name);

  return (
    <StepLayout
      footer={
        <>
          <Button variant="filled" css={{ w: "100%" }} size="lg" color="primary" type="submit" form={id}>
            Continue
          </Button>
        </>
      }
    >
      <Title css={{ pb: 16 }}>Your Account</Title>
      <form
        id={id}
        onSubmit={e => {
          e.preventDefault();
          if (!givenName.trim() || !familyName.trim()) return;

          onContinue({
            ...account,
            given_name: givenName,
            family_name: familyName,
          });
        }}
      >
        <Box css={{ spaceY: 24 }}>
          <Field>
            <Label>First name</Label>
            <Input
              autoFocus
              required
              size="lg"
              autoComplete="given-name"
              value={givenName}
              onValueChange={setGivenName}
            />
          </Field>
          <Field>
            <Label>Last name</Label>
            <Input required size="lg" autoComplete="family-name" value={familyName} onValueChange={setFamilyName} />
          </Field>
        </Box>
      </form>
    </StepLayout>
  );
}

function isValidPassword(password: string): boolean {
  return password.length >= 8;
}

function PasswordStep({ account, onContinue }: StepProps) {
  const id = useId();

  const [password, setPassword] = useState(account.password);
  const [showPassword, setShowPassword] = useState(false);

  function handleCreateAccount() {
    onContinue({
      ...account,
      password,
    });
  }

  return (
    <StepLayout
      footer={
        <>
          <Button
            variant="filled"
            color="primary"
            size="lg"
            css={{ w: "100%" }}
            type="submit"
            disabled={!isValidPassword(password)}
            form={id}
          >
            Continue
          </Button>
        </>
      }
    >
      <Title css={{ mb: 16 }}>Choose a password</Title>
      <Form
        id={id}
        onSubmit={() => {
          handleCreateAccount();
        }}
      >
        <Box css={{ spaceY: 24 }}>
          <Field css={{ "@maxSm": { display: "none" } }}>
            <Label>Sign-in email</Label>
            <Box
              css={{
                px: 16,
                py: 12,
                rounded: 8,
                backgroundColor: colors.warmGrey,
                color: colors.text2,
                fontScale: "lg",
              }}
            >
              {account.email}
            </Box>
          </Field>
          <Field>
            <Label>Password</Label>
            <Input
              autoFocus
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              size="lg"
              value={password}
              onValueChange={setPassword}
              rightIcon={
                <IconButton
                  variant="plain"
                  color="neutral"
                  size="md"
                  css={{ pointerEvents: "auto" }}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <InvisibleIcon /> : <VisibleIcon />}
                </IconButton>
              }
            />
          </Field>
        </Box>
        <Suspense fallback={null}>
          <PasswordStrengthBar password={password} />
        </Suspense>
      </Form>
    </StepLayout>
  );
}

function LocationStep({ account, countryCode, onCreate, status }: StepProps) {
  const id = useId();
  const [placeName, setPlaceName] = useState(account.approximate_location.place_name);
  const [placeCoords, setPlaceCoords] = useState(account.approximate_location.coords);

  const [locationOpen, setLocationOpen] = useState(false);

  function handleLocationResult(name: string, coords: { latitude: number; longitude: number }) {
    setPlaceName(name);
    setPlaceCoords(coords);
    setLocationOpen(false);
  }

  return (
    <StepLayout
      footer={
        <>
          <PrivacyNotice>Your current location will never be revealed to anyone.</PrivacyNotice>
          <Button
            variant="filled"
            css={{ w: "100%" }}
            size="lg"
            color="primary"
            type="submit"
            form={id}
            disabled={!placeName}
            loading={status === "pending"}
          >
            Create Account
          </Button>
        </>
      }
    >
      <Title css={{ mb: 16 }}>Set your location</Title>
      <Description css={{ mb: 24 }}>
        This will help ensure you see providers closest to you. Don’t worry, you can change your location at anytime.
      </Description>
      <form
        id={id}
        onSubmit={e => {
          e.preventDefault();
          if (!placeName) return;

          onCreate({
            ...account,
            approximate_location: { coords: placeCoords, place_name: placeName },
          });
        }}
      >
        <Box css={{ spaceY: 24 }}>
          <Field>
            <Input
              required
              size="lg"
              readOnly
              value={placeName}
              className="ph-clear"
              placeholder="Search for location"
              rightIcon={<MagnifyingGlassIcon />}
              onClick={() => setLocationOpen(true)}
            />
          </Field>
        </Box>
      </form>

      <LocationPickerModal
        open={locationOpen}
        onOpenChange={setLocationOpen}
        onLocationChange={handleLocationResult}
        countryCode={countryCode}
        dontSave
      />
    </StepLayout>
  );
}

function PrivacyNotice({ children }: { children?: ReactNode }) {
  return (
    <Flex css={{ items: "center", fontScale: "sm", gap: 12 }}>
      <PadlockIcon style={{ flexShrink: 0 }} width="14" height="14" />
      <Text css={{ flex: "1 0 0px" }}>{children}</Text>
    </Flex>
  );
}
